import React, { useCallback, useEffect, useState } from 'react'

import { PageLink, PageTitle } from '../../../../../_metronic/layout/core'
import { TablePagination } from '../../../../../components/Table/TablePagination'
import { FilterManager } from '../../../../common/filter/FilterManager'
import { getMerchantLookupWithToken } from '../../../../common/core/_requests'
import { getErrorResponse } from '../../../../common/api/errors'
import { getPaymentMethods } from '../../../settings/api/getPaymentMethods'
import { getFlowControlRefusals } from '../../../ticket-card/core/refusalApi'
import { FlowControlRefusalListItem } from '../../../ticket-card/core/refusal.types'
import { matchingCapabilities } from '../../core/_models'
import { breadcrumbs as payoutTabs } from '../../components/payouts-active/config'
import FlowControlRefusalsTable from './components/FlowControlRefusalsTable'
import { defaultRefusalFilters, MULTI_SELECT_PROPS } from '../../../payout-tickets/refusals/core/consts'
import { MATCHING_REASON_OPTIONS, OUTCOME_OPTIONS, PAGE_SIZE, STAGE_OPTIONS } from './core/consts'
import { FlowControlRefusalFilterState, toFlowControlRefusalParams } from './core/types'

type Option = { value: string; label: string }

const BREADCRUMBS: PageLink[] = [
  ...payoutTabs
    .filter((tab) => tab.path !== '/matching/payout/refusals')
    .map((tab) => ({ ...tab, isActive: false })),
  {
    title: 'Отказы',
    path: '/matching/payout/refusals',
    isSeparator: false,
    isActive: true,
    roles: matchingCapabilities.canViewPayoutRefusals,
  },
]

const PayoutRefusalsPage: React.FC = () => {
  // Никогда не весь журнал: исход отказа выводится из двух других таблиц, и по всему журналу это
  // секунды. Сутки считаются при открытии страницы, сброс возвращает к ним же.
  const [defaults] = useState<FlowControlRefusalFilterState>(defaultRefusalFilters)
  const [items, setItems] = useState<FlowControlRefusalListItem[]>([])
  const [totalRows, setTotalRows] = useState(0)
  const [page, setPage] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [applied, setApplied] = useState<FlowControlRefusalFilterState>(defaults)
  const [merchantOptions, setMerchantOptions] = useState<Option[]>([])
  const [paymentOptions, setPaymentOptions] = useState<Option[]>([])

  useEffect(() => {
    getMerchantLookupWithToken()
      .then((resp) => {
        if (resp.data?.success) setMerchantOptions(resp.data.data ?? [])
      })
      .catch(() => setMerchantOptions([]))

    getPaymentMethods()
      .then((resp: any) => {
        if (!resp?.data?.success) return
        const list = resp.data.paymentMethods ?? []
        setPaymentOptions(list.map((pm: any) => ({ value: pm.type, label: pm.type })))
      })
      .catch(() => setPaymentOptions([]))
  }, [])

  const load = useCallback(async (pg: number, filters: FlowControlRefusalFilterState) => {
    setLoading(true)
    setError(null)
    try {
      const { data } = await getFlowControlRefusals(toFlowControlRefusalParams(filters, pg + 1))
      if (!data.success) {
        setItems([])
        setTotalRows(0)
        setError(data.errors.general_error ?? 'Не удалось загрузить отказы')
        return
      }
      setItems(data.data ?? [])
      setTotalRows(data.totalRows ?? 0)
    } catch (e) {
      setItems([])
      setTotalRows(0)
      setError(getErrorResponse<{ general_error?: string }>(e)?.general_error ?? 'Не удалось загрузить отказы')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load(0, defaults)
  }, [load, defaults])

  const handleApply = (filters: FlowControlRefusalFilterState) => {
    setApplied(filters)
    setPage(0)
    load(0, filters)
  }

  const handlePageChange = (selected: number) => {
    setPage(selected)
    load(selected, applied)
  }

  return (
    <>
      <PageTitle breadcrumbs={BREADCRUMBS}>Отказы мэтчинга</PageTitle>
      <FilterManager<FlowControlRefusalFilterState>
        activeFilters={[
          'dateRange',
          { filter: 'search', fieldName: 'transactionId' },
          { filter: 'selectMulti', fieldName: 'merchantTokens' },
          { filter: 'selectMulti', fieldName: 'paymentMethodTypes' },
          { filter: 'selectMulti', fieldName: 'stages' },
          { filter: 'selectMulti', fieldName: 'outcomes' },
          { filter: 'selectMulti', fieldName: 'matchingReasons' },
          'amountRange',
        ]}
        defaultValues={defaults}
        filterProps={{
          transactionId: { label: 'ID транзакции мерчанта', placeholder: 'transactionId' },
          dateRange: { ranges: [] },
          merchantTokens: { ...MULTI_SELECT_PROPS, label: 'Мерчант', options: merchantOptions },
          paymentMethodTypes: { ...MULTI_SELECT_PROPS, label: 'Платёжный метод', options: paymentOptions },
          stages: { ...MULTI_SELECT_PROPS, label: 'Стадия', options: STAGE_OPTIONS },
          outcomes: { ...MULTI_SELECT_PROPS, label: 'Исход', options: OUTCOME_OPTIONS },
          matchingReasons: { ...MULTI_SELECT_PROPS, label: 'Причина', options: MATCHING_REASON_OPTIONS },
        }}
        onApplyFilters={handleApply}
      />
      <div className='card'>
        <div className='card-body'>
          {error && <div className='alert alert-danger'>{error}</div>}
          <FlowControlRefusalsTable items={items} loading={loading} />
          <TablePagination
            currentPage={page}
            pageCount={Math.ceil(totalRows / PAGE_SIZE)}
            onPageChange={handlePageChange}
          />
        </div>
      </div>
    </>
  )
}

export default PayoutRefusalsPage
